import { type ActionReducerMapBuilder, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isCanceledError } from 'src/helper/api';
import { parseApiError } from 'src/helper/utils';
import {
  getEcfEventDetails,
  getEcfEventLifecycle,
  getEcfEvents,
  getEcfLumiConfig,
  getEcfOverview,
  getEcfStatus,
  getEcfYardiMappings,
} from './api';
import {
  type EcfEventDetail,
  type EcfEventsResponse,
  type EcfOverview,
  type EcfOverviewResponse,
  type EcfStatus,
  type EcfWorkflowOverview,
  type ExternalConnectionsState,
  initialState,
  type LumiConfig,
  type LumiTimeSlot,
  type YardiMappingsConfig,
  type YardiMappingsConfigResponse,
} from './types';

const EMPTY_WORKFLOW_OVERVIEW: EcfWorkflowOverview = Object.freeze({
  total: 0,
  resolved: 0,
  active: 0,
  needs_review: 0,
  options: [],
  active_details: { count: 0, recent: [] },
  needs_review_details: { count: 0, recent: [] },
});

const normalizeEcfOverview = (overview: EcfOverviewResponse): EcfOverview => ({
  ...overview,
  workflows: overview.workflows ?? EMPTY_WORKFLOW_OVERVIEW,
});

/**
 * Slice-key for a LuMi area. The PUT path uses the hyphenated area
 * ("blackout-dates") while the config object key is underscored.
 */
type LumiConfigKey = keyof Omit<LumiConfig, '_errors'>;

/** Extracts a human-readable error, returning null for cancelled requests. */
function toError(payload: unknown): string | null {
  if (isCanceledError(payload)) return null;
  return parseApiError(payload);
}

const optionKeys = (options: unknown): string[] =>
  (Array.isArray(options) ? options : []).flatMap((option: unknown) => {
    if (typeof option === 'string') return [option];
    if (!option || typeof option !== 'object') return [];
    const key = (option as { key?: unknown }).key;
    return typeof key === 'string' ? [key] : [];
  });

const DAY_ALIASES: Record<string, string> = {
  mon: 'monday',
  tue: 'tuesday',
  wed: 'wednesday',
  thu: 'thursday',
  fri: 'friday',
  sat: 'saturday',
  sun: 'sunday',
};

const normalizeAvailableDays = (days: unknown): string[] =>
  Array.from(
    new Set(
      (Array.isArray(days) ? days : [])
        .filter((day): day is string => typeof day === 'string')
        .map((day) => day.trim().toLowerCase())
        .map((day) => DAY_ALIASES[day] ?? day),
    ),
  );

/**
 * Older LuMi tenant rows stored only start/end. Give those windows deterministic
 * keys and editable labels so an administrator can repair and save them through
 * the current complete contract.
 */
const normalizeTimeSlots = (slots: unknown): LumiTimeSlot[] =>
  (Array.isArray(slots) ? slots : [])
    .filter((slot): slot is Record<string, unknown> => !!slot && typeof slot === 'object')
    .map((slot, index) => {
      const start = typeof slot.start === 'string' ? slot.start : '';
      const end = typeof slot.end === 'string' ? slot.end : '';
      const rawKey = typeof slot.key === 'string' ? slot.key.trim() : '';
      const rawLabel = typeof slot.label === 'string' ? slot.label.trim() : '';
      const generatedKey = `window-${start.replace(':', '') || 'start'}-${
        end.replace(':', '') || 'end'
      }-${index + 1}`;

      return {
        key: rawKey || generatedKey,
        label: rawLabel || `Time window ${index + 1}`,
        start,
        end,
        ...(typeof slot.emergency_enabled === 'boolean'
          ? { emergency_enabled: slot.emergency_enabled }
          : {}),
      };
    });

/**
 * LuMi's access GET enriches enabled options with labels and descriptions while its PUT accepts
 * the option keys. Keep that serialization detail out of the forms so a GET can be saved safely.
 */
function normalizeLumiConfig(input: LumiConfig): LumiConfig {
  return {
    ...input,
    availability: input.availability
      ? {
          ...input.availability,
          available_days: normalizeAvailableDays(input.availability.available_days),
          time_slots: normalizeTimeSlots(input.availability.time_slots),
        }
      : input.availability,
    access: input.access
      ? {
          ...input.access,
          enabled_entry_types: optionKeys(input.access.enabled_entry_types),
          enabled_pet_types: optionKeys(input.access.enabled_pet_types),
        }
      : input.access,
  };
}

const emptyPriorityMappings = () => ({
  inbound_mapping: {},
  outbound_mapping: { urgent: null, high: null, medium: null, low: null },
  emergency_priorities: [],
});

/** Keeps a frontend-first priority rollout safe against the previous GET shape. */
function normalizeYardiMappings(input: YardiMappingsConfigResponse): YardiMappingsConfig {
  return {
    ...input,
    catalog: {
      ...input.catalog,
      priorities: Array.isArray(input.catalog.priorities) ? input.catalog.priorities : [],
    },
    readiness: {
      ...input.readiness,
      priority_inbound_total: input.readiness.priority_inbound_total ?? 0,
      priority_inbound_mapped: input.readiness.priority_inbound_mapped ?? 0,
      priority_outbound_total: input.readiness.priority_outbound_total ?? 0,
      priority_outbound_mapped: input.readiness.priority_outbound_mapped ?? 0,
    },
    priority: input.priority ?? emptyPriorityMappings(),
  };
}

const externalConnectionsSlice = createSlice({
  name: 'externalConnections',
  initialState,
  reducers: {
    clearEventDetail: (state: ExternalConnectionsState) => {
      state.eventDetail = null;
      state.eventDetailError = null;
      state.eventDetailLoading = false;
      // The lifecycle belongs to the closed drawer — dropping it here stops a reopen on a
      // DIFFERENT entity from briefly rendering the previous entity's history.
      state.lifecycle = [];
      state.lifecycleError = null;
      state.lifecycleLoading = false;
      state.lifecycleWorkOrderUuid = null;
      state.lifecycleTotal = 0;
    },
    /**
     * Records which work order the lifecycle is being loaded for. Dispatched immediately before
     * the fetch so that stepping through events on the SAME entity (clicking a timeline row)
     * reuses the loaded history instead of refetching it on every detail swap.
     */
    setLifecycleTarget: (state: ExternalConnectionsState, action: PayloadAction<string | null>) => {
      state.lifecycleWorkOrderUuid = action.payload;
    },
    /** Keeps the slice the source of truth after a successful per-area PUT. */
    setLumiConfigArea: (
      state: ExternalConnectionsState,
      action: PayloadAction<{ key: LumiConfigKey; value: LumiConfig[LumiConfigKey] }>,
    ) => {
      if (!state.lumiConfig) return;
      const { key, value } = action.payload;
      state.lumiConfig = { ...state.lumiConfig, [key]: value };
    },
    setYardiMappings: (
      state: ExternalConnectionsState,
      action: PayloadAction<YardiMappingsConfigResponse>,
    ) => {
      state.yardiMappings = normalizeYardiMappings(action.payload);
      state.yardiMappingsError = null;
    },
    clearExternalConnections: () => initialState,
  },
  extraReducers: (builder: ActionReducerMapBuilder<ExternalConnectionsState>) => {
    // ----- STATUS -----
    builder.addCase(getEcfStatus().pending, (state) => {
      state.statusLoading = true;
      state.statusError = null;
    });
    builder.addCase(getEcfStatus().fulfilled, (state, action) => {
      state.status = action.payload as EcfStatus;
      state.statusLoading = false;
    });
    builder.addCase(getEcfStatus().rejected, (state, action) => {
      const message = toError(action.payload);
      if (message === null) return; // cancelled — leave loading as-is for the retry
      state.statusError = message;
      state.statusLoading = false;
    });

    // ----- OVERVIEW -----
    builder.addCase(getEcfOverview().pending, (state) => {
      state.overviewLoading = true;
      state.overviewError = null;
    });
    builder.addCase(getEcfOverview().fulfilled, (state, action) => {
      state.overview = normalizeEcfOverview(action.payload as EcfOverviewResponse);
      state.overviewLoading = false;
    });
    builder.addCase(getEcfOverview().rejected, (state, action) => {
      const message = toError(action.payload);
      if (message === null) return;
      state.overviewError = message;
      state.overviewLoading = false;
    });

    // ----- EVENT LEDGER -----
    builder.addCase(getEcfEvents().pending, (state) => {
      state.eventsLoading = true;
      state.eventsError = null;
    });
    builder.addCase(getEcfEvents().fulfilled, (state, action) => {
      const { data, meta } = action.payload as EcfEventsResponse;
      state.events = data ?? [];
      state.eventsMeta = meta ?? null;
      state.eventsLoading = false;
    });
    builder.addCase(getEcfEvents().rejected, (state, action) => {
      const message = toError(action.payload);
      if (message === null) return;
      state.eventsError = message;
      state.eventsLoading = false;
    });

    // ----- EVENT DETAIL -----
    builder.addCase(getEcfEventDetails('').pending, (state) => {
      state.eventDetailLoading = true;
      state.eventDetailError = null;
    });
    builder.addCase(getEcfEventDetails('').fulfilled, (state, action) => {
      state.eventDetail = action.payload as EcfEventDetail;
      state.eventDetailLoading = false;
    });
    builder.addCase(getEcfEventDetails('').rejected, (state, action) => {
      const message = toError(action.payload);
      if (message === null) return;
      state.eventDetailError = message;
      state.eventDetailLoading = false;
    });

    // ----- EVENT LIFECYCLE (one entity's history; separate from the ledger list) -----
    builder.addCase(getEcfEventLifecycle().pending, (state) => {
      state.lifecycleLoading = true;
      state.lifecycleError = null;
    });
    builder.addCase(getEcfEventLifecycle().fulfilled, (state, action) => {
      const { data, meta } = action.payload as EcfEventsResponse;
      state.lifecycle = data ?? [];
      state.lifecycleTotal = meta?.total ?? data?.length ?? 0;
      state.lifecycleLoading = false;
    });
    builder.addCase(getEcfEventLifecycle().rejected, (state, action) => {
      const message = toError(action.payload);
      if (message === null) return;
      state.lifecycleError = message;
      state.lifecycleLoading = false;
    });

    // ----- LUMI CONFIG -----
    builder.addCase(getEcfLumiConfig().pending, (state) => {
      state.lumiConfigLoading = true;
      state.lumiConfigError = null;
    });
    builder.addCase(getEcfLumiConfig().fulfilled, (state, action) => {
      state.lumiConfig = normalizeLumiConfig(action.payload as LumiConfig);
      state.lumiConfigLoading = false;
    });
    builder.addCase(getEcfLumiConfig().rejected, (state, action) => {
      const message = toError(action.payload);
      if (message === null) return;
      state.lumiConfigError = message;
      state.lumiConfigLoading = false;
    });

    // ----- YARDI MAPPINGS -----
    builder.addCase(getEcfYardiMappings(0).pending, (state) => {
      state.yardiMappingsLoading = true;
      state.yardiMappingsError = null;
    });
    builder.addCase(getEcfYardiMappings(0).fulfilled, (state, action) => {
      state.yardiMappings = normalizeYardiMappings(action.payload as YardiMappingsConfigResponse);
      state.yardiMappingsLoading = false;
    });
    builder.addCase(getEcfYardiMappings(0).rejected, (state, action) => {
      const message = toError(action.payload);
      if (message === null) return;
      state.yardiMappingsError = message;
      state.yardiMappingsLoading = false;
    });
  },
});

export const {
  clearEventDetail,
  setLifecycleTarget,
  setLumiConfigArea,
  setYardiMappings,
  clearExternalConnections,
} =
  externalConnectionsSlice.actions;

export default externalConnectionsSlice.reducer;
